import { lazy, Suspense, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { config, DEMO_BASE, isDemo } from '../config'
import { ApiError, createApi } from '../infra/api'
import { ApiContext, useApi } from './api-context'
import { AdminShell } from './AdminShell'
import { FounderPage } from './pages/FounderPage'
import { NewFounderPage } from './pages/NewFounderPage'
import { NewWebhookPage } from './pages/NewWebhookPage'
import { NotInvited } from './pages/NotInvited'
import { RunPage } from './pages/RunPage'
import { SharedWithMe } from './pages/SharedWithMe'
import { Screen } from './pages/Screen'
import { useSession } from './session'
import { useLoad } from './useLoad'
import { homeFor } from '../domain/route'
import { FounderApp } from './founder/FounderApp'
import { LearnManual } from './founder/Learn'
import { RouterNav } from './RouterNav'
import { sections } from '../sections'
import { EmptyState, Screen as Page } from '../ui'

/**
 * The /__ui board: dev builds only. With DEV false the ternary folds to null at build time, so the board's chunk is
 * never emitted (scripts/check-dist.ts proves it on every build).
 */
const UiBoard = import.meta.env.DEV ? lazy(() => import('../board/Board')) : null
const onBoard = () => location.pathname === '/__ui' || location.pathname.startsWith('/__ui/')

export function App({ apiFetch }: { apiFetch?: typeof fetch }) {
  return (
    <BrowserRouter basename={isDemo() ? DEMO_BASE : undefined}>
      <RouterNav>
        <AuthGate>
          <ApiGate apiFetch={apiFetch}>
            <AdminRoutes />
          </ApiGate>
        </AuthGate>
      </RouterNav>
    </BrowserRouter>
  )
}

/** Every registry entry with a page, loaded when first opened (src/sections.ts drives the nav and these routes). */
const SECTION_ROUTES = sections.flatMap((x) => ('load' in x ? [{ key: x.key, path: x.path, title: x.title, Page: lazy(x.load) }] : []))

/** While a section's code loads: its title and the page's loading shape. */
function SectionLoading({ title }: { title: string }) {
  return (
    <Page title={title} query={{ data: undefined, error: undefined, loading: true, reload: () => {} }}>
      {() => null}
    </Page>
  )
}

function AdminRoutes() {
  return (
    <Routes>
      {SECTION_ROUTES.map(({ key, path, title, Page: Section }) => (
        <Route
          key={key}
          path={path}
          element={
            <Suspense fallback={<SectionLoading title={title} />}>
              <Section />
            </Suspense>
          }
        />
      ))}
      <Route path="/alerts/webhooks/new" element={<NewWebhookPage />} />
      <Route path="/founders/new" element={<NewFounderPage />} />
      <Route path="/founders/:id" element={<FounderPage />} />
      <Route path="/runs/:id" element={<RunPage />} />
      <Route path="/learn/*" element={<AdminLearn />} />
      <Route path="/callback" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

/** Admins read the same manual founders do (at /learn; not in the admin nav). */
function AdminLearn() {
  return <LearnManual api={useApi().api} />
}

/** cpg.computer's /signup sends people to `/?signup=1`: straight on to sign-up instead of the sign-in screen. */
const wantsSignup = () => new URLSearchParams(location.search).has('signup')

function AuthGate({ children }: { children: ReactNode }) {
  const s = useSession()
  const signup = !s.isLoading && !s.isAuthenticated && !s.error && wantsSignup()
  const sent = useRef(false)
  useEffect(() => {
    if (signup && !sent.current) (sent.current = true), s.register()
  }, [signup, s])
  if (s.isLoading || signup) return <Screen title="One moment." quiet />
  if (!s.isAuthenticated) {
    return (
      <Screen
        title="Hello."
        action={
          <button className="btn btn-ink" type="button" onClick={s.login}>
            Sign in
          </button>
        }
      >
        {s.error ? `Sign-in didn't finish (${s.error}). Try again.` : 'Sign in to reach your computer.'}
      </Screen>
    )
  }
  return <>{children}</>
}

function ApiGate({ apiFetch, children }: { apiFetch?: typeof fetch; children: ReactNode }) {
  const s = useSession()
  const api = useMemo(() => createApi({ baseUrl: config.mock ? '' : config.apiUrl, getToken: s.getToken, fetch: apiFetch }), [s.getToken, apiFetch])
  const me = useLoad(() => api.me(), [api])
  const signOut = (
    <button className="btn btn-soft" type="button" onClick={s.logout}>
      Sign out
    </button>
  )

  if (me.error) {
    const e = me.error
    const who = s.email ?? 'this account'
    if (e instanceof ApiError && e.status === 403 && e.code === 'not_invited') {
      return <NotInvited api={api} email={e.email ?? s.email} action={signOut} onInvited={me.reload} />
    }
    if (e instanceof ApiError && e.status === 403 && e.code === 'no_email_claim') {
      return (
        <Screen title="We couldn't tell who you are." action={signOut}>
          You're signed in, but the sign-in didn't include an email address, so cpg.computer can't match it to a person.
          This is a setting on our side; let Johnnie know.
        </Screen>
      )
    }
    if (e instanceof ApiError && e.status === 403) {
      return (
        <Screen title="Not on the list yet." action={signOut}>
          You're signed in as <b>{who}</b>, but this account isn't on cpg.computer yet.
        </Screen>
      )
    }
    if (e instanceof ApiError && e.status === 401) {
      return (
        <Screen
          title="Please sign in again."
          action={
            <button className="btn btn-ink" type="button" onClick={s.login}>
              Sign in
            </button>
          }
        >
          Your session couldn't be verified.
        </Screen>
      )
    }
    return (
      <Screen
        title="The control plane didn't answer."
        action={
          <button className="btn btn-soft" type="button" onClick={me.reload}>
            Try again
          </button>
        }
      >
        {e.message}
      </Screen>
    )
  }
  if (!me.data) return <Screen title="One moment." quiet />
  const home = homeFor(me.data)
  if (home.kind === 'founder') return <FounderApp api={api} me={me.data} />
  if (home.kind === 'viewer') return <SharedWithMe api={api} email={me.data.person.email} onSignOut={s.logout} />
  if (home.kind === 'unlinked') {
    return (
      <Screen title="Your computer isn't set up yet." action={signOut}>
        You're signed in as <b>{me.data.person.email}</b>, but there's no computer on this account yet. We'll be in touch.
      </Screen>
    )
  }
  if (UiBoard && onBoard()) {
    return (
      <ApiContext.Provider value={{ api, me: me.data }}>
        <Suspense fallback={<Screen title="One moment." quiet />}>
          <UiBoard />
        </Suspense>
      </ApiContext.Provider>
    )
  }
  // The AppShell keeps its own ErrorBoundary around the routed page, so the nav stays usable when a page breaks.
  return (
    <ApiContext.Provider value={{ api, me: me.data }}>
      <AdminShell>{children}</AdminShell>
    </ApiContext.Provider>
  )
}

function NotFound() {
  return (
    <Page title="Nothing here.">
      <EmptyState title="That page doesn't exist.">Pick a section from the menu, or search with ⌘K.</EmptyState>
    </Page>
  )
}
